import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { X, Lock, CheckCircle2, Ban, Loader2, ExternalLink } from 'lucide-react';
import { computeQuality, qualityBand, qualityColorClasses } from '@/lib/qualityScore';
import { QualityBreakdownBody } from '@/components/QualityBreakdownPopover';
import TierBadge from '@/components/TierBadge';
import CapitalScoreBadge from '@/components/CapitalScoreBadge';
import PlusUpsellModal from '@/components/PlusUpsellModal';
import NewsWatchStar from '@/components/NewsWatchStar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ND_TIER_CLS = {
  'GROWTH-S': 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/40',
  'GROWTH-A': 'bg-sky-500/15 text-sky-700 dark:text-sky-300 ring-sky-500/40',
  'GROWTH-B': 'bg-blue-500/15 text-blue-700 dark:text-blue-300 ring-blue-500/40',
  'GROWTH-C': 'bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/40',
  SPECULATIVE: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-rose-500/40',
};

const fmtPrice = (p) => (p == null ? '—' : `$${Number(p).toFixed(2)}`);

const DivQualityPill = ({ stock, onClick }) => {
  const score = computeQuality(stock)?.score ?? null;
  const band = qualityBand(score);
  const cls = qualityColorClasses(band.tone);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ring-1 text-[10px] font-bold uppercase tracking-wider cursor-pointer hover:ring-2 transition-all ${cls.bg} ${cls.text} ${cls.ring}`}
      title={`Dividend Quality · ${score ?? '—'}/100 (${band.label}) — tap for the full breakdown`}
      data-testid={`quality-badge-sector-example-${stock.ticker}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cls.dot}`} />
      {score ?? '—'}<span className="opacity-70 font-mono">{band.short}</span>
    </button>
  );
};

const NdQualityPill = ({ score, tier, ticker, onClick }) => {
  if (!tier) return null;
  const short = tier === 'SPECULATIVE' ? 'SPEC' : tier.replace('GROWTH-', 'G-');
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ring-1 text-[10px] font-bold uppercase tracking-wider cursor-pointer hover:ring-2 transition-all ${ND_TIER_CLS[tier] || 'bg-muted text-muted-foreground ring-border'}`}
      title={`Non-Div Growth Quality · ${score ?? '—'}/100 (${tier}) — click for the full breakdown`}
      data-testid={`nd-quality-badge-sector-example-${ticker}`}
    >
      {score ?? '—'}<span className="opacity-70 font-mono">{short}</span>
    </button>
  );
};

const StockRow = ({ s, onOpen, onBreakdown, kind }) => (
  <div className="flex items-center gap-1 pl-1 pr-2.5 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/70 transition-colors" data-testid={`sector-example-row-${kind}-${s.ticker}`}>
    <NewsWatchStar ticker={s.ticker} isDividend={kind === 'div'} variant="table" />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          type="button"
          onClick={() => onOpen?.(s.ticker)}
          className="font-mono text-xs font-bold text-yellow-600 dark:text-yellow-400 hover:underline"
          title={`Open ${s.ticker}`}
          data-testid={`sector-example-open-${kind}-${s.ticker}`}
        >
          {s.ticker}
        </button>
        {kind === 'div' && s.dividend_tier && <TierBadge tier={s.dividend_tier} showLabel={false} />}
        {kind === 'div' ? (
          <DivQualityPill stock={s} onClick={() => onBreakdown(s)} />
        ) : (
          <>
            <NdQualityPill score={s.non_dividend_quality_score} tier={s.non_dividend_quality_tier} ticker={s.ticker} onClick={() => onOpen?.(s.ticker, 'non-div-growth')} />
            {s.equity_quality?.rank != null && (
              <CapitalScoreBadge rank={s.equity_quality.rank} verdict={s.equity_quality.verdict} verdictLabel={s.equity_quality.verdict_label} size="xs" ticker={s.ticker} testIdSuffix="sector-example" onClick={() => onOpen?.(s.ticker, 'capital-analytics')} />
            )}
          </>
        )}
      </div>
      <button
        type="button"
        onClick={() => onOpen?.(s.ticker)}
        className="block max-w-full text-left text-[11px] text-slate-600 dark:text-slate-400 truncate hover:text-slate-900 dark:hover:text-slate-200"
        data-testid={`sector-example-name-${kind}-${s.ticker}`}
      >
        {s.name}
      </button>
    </div>
    <div className="text-right shrink-0">
      <div className="font-mono text-xs font-semibold text-slate-800 dark:text-slate-100">{fmtPrice(s.price)}</div>
      {kind === 'div' && s.dividend_yield != null && (
        <div className="font-mono text-[11px] text-emerald-600 dark:text-emerald-400">{Number(s.dividend_yield).toFixed(2)}% yld</div>
      )}
    </div>
  </div>
);

export default function SectorExamplesModal({ item, tone, onClose, onOpenStock }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [label, why, sectors] = item;

  useEffect(() => {
    setData(null);
    setError(false);
    axios
      .get(`${API}/dashboard/sector-examples`, { params: { sectors: sectors.join(',') }, withCredentials: true, timeout: 8000 })
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [sectors]);

  const isFavor = tone === 'emerald';
  const Icon = isFavor ? CheckCircle2 : Ban;
  const screenerHref = `/screener?sector=${encodeURIComponent(sectors[0])}`;

  return (
    <>
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="sector-examples-backdrop"
    >
      <div
        className="w-full max-w-lg max-h-[85vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 p-5 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="sector-examples-modal"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          aria-label="Close"
          data-testid="sector-examples-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider ${isFavor ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
          <Icon className="w-3.5 h-3.5" />
          {isFavor ? 'Favor' : 'Avoid'} · Sector Playbook
        </div>
        <h3 className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100" data-testid="sector-examples-title">{label}</h3>
        <p className="text-xs text-slate-600 dark:text-slate-400">{why}. Examples below are the highest-quality names in {sectors.join(' / ')} — {isFavor ? 'strong candidates to research' : 'the best of a sector history says to underweight, not a buy list'}.</p>

        {error && <div className="mt-4 text-xs text-rose-600" data-testid="sector-examples-error">Couldn’t load examples right now.</div>}
        {!data && !error && (
          <div className="mt-6 flex items-center justify-center text-slate-400" data-testid="sector-examples-loading"><Loader2 className="w-5 h-5 animate-spin" /></div>
        )}

        {data && (
          <>
            <div className="mt-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 px-2.5">Dividend payers · top Dividend Quality</div>
              {data.dividend.length === 0 ? (
                <div className="px-2.5 py-2 text-xs text-slate-500" data-testid="sector-examples-div-empty">No scored dividend payers in this sector yet.</div>
              ) : (
                <div className="mt-1 divide-y divide-slate-100 dark:divide-slate-800" data-testid="sector-examples-div-list">
                  {data.dividend.map((s) => <StockRow key={s.ticker} s={s} kind="div" onOpen={onOpenStock} onBreakdown={setBreakdown} />)}
                </div>
              )}
            </div>

            <div className="mt-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 px-2.5">Non-dividend · top Growth Quality</div>
              {data.restricted ? (
                <button
                  type="button"
                  onClick={() => setShowUpsell(true)}
                  className="mt-1 mx-2.5 inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 ring-1 ring-amber-500/30 hover:bg-amber-500/25 transition-colors"
                  data-testid="sector-examples-plus-lock"
                >
                  <Lock className="w-3 h-3" />
                  {data.non_dividend_available} non-dividend example{data.non_dividend_available === 1 ? '' : 's'} · Plus
                </button>
              ) : data.non_dividend.length === 0 ? (
                <div className="px-2.5 py-2 text-xs text-slate-500" data-testid="sector-examples-nd-empty">No scored non-dividend names in this sector yet.</div>
              ) : (
                <div className="mt-1 divide-y divide-slate-100 dark:divide-slate-800" data-testid="sector-examples-nd-list">
                  {data.non_dividend.map((s) => <StockRow key={s.ticker} s={s} kind="nd" onOpen={onOpenStock} />)}
                </div>
              )}
            </div>

            <Link
              to={screenerHref}
              className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-sky-600 dark:text-sky-400 hover:underline"
              data-testid="sector-examples-screener-link"
            >
              Open {sectors[0]} in the Screener <ExternalLink className="w-3 h-3" />
            </Link>
          </>
        )}
      </div>
    </div>
    {breakdown && (
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
        onClick={(e) => { e.stopPropagation(); setBreakdown(null); }}
        data-testid="quality-breakdown-overlay"
      >
        <div
          className="relative w-80 max-w-full max-h-[80vh] overflow-y-auto overscroll-contain rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl p-4 pt-8"
          onClick={(e) => e.stopPropagation()}
          data-testid={`quality-popover-${breakdown.ticker}`}
        >
          <button
            type="button"
            onClick={() => setBreakdown(null)}
            className="absolute top-2 right-2 p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Close breakdown"
            data-testid="quality-breakdown-close"
          >
            <X className="w-4 h-4" />
          </button>
          <QualityBreakdownBody stock={breakdown} />
        </div>
      </div>
    )}
    <PlusUpsellModal open={showUpsell} onClose={() => setShowUpsell(false)} />
    </>
  );
}
